import React, { useState, useEffect, useRef, useMemo } from "react";
import { DataGrid, UploadModal, NGODatePicker, Modal, DeleteConf } from "../../../common";
import { AgGridReact } from "ag-grid-react";
import CustomSelect from "../../../common/CustomSelect";
import { APIEndpoint } from "../../../utils/axios";
import usePermissions from "../../../utils/userPermissions";
import moment from "moment";
import { saveAs } from "file-saver";
import uploadItemIcon from "../../../assets/icons/upload-items.svg";
import downloadMasterIcon from "../../../assets/icons/download-master.svg";
import { getCultureFromLs } from "../../../utils/commonFunctions";
import viewIcon from "../../../assets/view.svg";
import editIcon2 from "../../../assets/edit2.svg";
import deleteIcon from "../../../assets/delete.svg";
import currencyCodes from "currency-codes";

const expandReciprocalRates = (items) => {
  if (!Array.isArray(items)) return [];
  const result = [];
  const seenPairs = new Set();
  for (const item of items) {
    if (item && item.fromIsoCode && item.toIsoCode && item.fromIsoCode !== item.toIsoCode) {
      result.push(item);
      seenPairs.add(`${item.fromIsoCode}->${item.toIsoCode}`);
    }
  }
  for (const item of items) {
    if (
      item &&
      item.fromIsoCode &&
      item.toIsoCode &&
      item.fromIsoCode !== item.toIsoCode &&
      item.rate > 0 &&
      !seenPairs.has(`${item.toIsoCode}->${item.fromIsoCode}`)
    ) {
      result.push({
        fromIsoCode: item.toIsoCode,
        isoCode: item.toIsoCode,
        toIsoCode: item.fromIsoCode,
        rate: Number((1 / item.rate).toFixed(8))
      });
    }
  }
  return result;
};

const filterRatesByDirection = (rawItems, direction) => {
  const items = expandReciprocalRates(rawItems);
  let baseCurrency = getCultureFromLs()?.currency?.code;
  const toCounts = {};
  items.forEach((it) => {
    if (it.toIsoCode && it.fromIsoCode !== it.toIsoCode) {
      toCounts[it.toIsoCode] = (toCounts[it.toIsoCode] || 0) + 1;
    }
  });
  const sortedTo = Object.keys(toCounts).sort(
    (a, b) => toCounts[b] - toCounts[a],
  );
  if ((!baseCurrency || !toCounts[baseCurrency]) && sortedTo.length > 0) {
    baseCurrency = sortedTo[0];
  }

  // Filter out same-currency pairs (e.g. KWD -> KWD)
  const validItems = items.filter((item) => item.fromIsoCode && item.toIsoCode && item.fromIsoCode !== item.toIsoCode);

  if (direction === "foreign_to_base") {
    return validItems.filter(
      (item) =>
        item.toIsoCode === baseCurrency && item.fromIsoCode !== baseCurrency,
    );
  } else if (direction === "base_to_foreign") {
    return validItems.filter((item) => item.fromIsoCode === baseCurrency);
  }
  return validItems;
};

export const IbrExchangePage = () => {
  const [isReloadTable, setIsReloadTable] = useState(false);

  // Upload modal state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFileStatusBlock, setUploadFileStatusBlock] = useState("");
  const [uploadedFile, setUploadedFile] = useState([]);
  const [formDetails, setFormDetails] = useState({});

  // View modal state
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedRateListMeta, setSelectedRateListMeta] = useState(null);
  const [modalRates, setModalRates] = useState([]);
  const [modalDirection, setModalDirection] = useState("foreign_to_base");
  const [loadingModalRates, setLoadingModalRates] = useState(false);

  // Edit modal state (for upcoming rates)
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEditRateListMeta, setSelectedEditRateListMeta] = useState(null);
  const [editFormValues, setEditFormValues] = useState({ name: "", effFrom: "" });
  const [editModalRates, setEditModalRates] = useState([]);
  const [loadingEditRates, setLoadingEditRates] = useState(false);
  const [editError, setEditError] = useState("");
  const [editSuccess, setEditSuccess] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  // Add row state inside Edit Modal
  const [showAddRowBar, setShowAddRowBar] = useState(false);
  const [newRowData, setNewRowData] = useState({ fromIsoCode: "", rate: "" });
  const [addRowError, setAddRowError] = useState("");

  // Delete modal state (for upcoming rates)
  const [deleteModalState, setDeleteModalState] = useState({
    show: false,
    item: null,
    loading: false,
    statusObj: null,
  });

  const allRateListsCache = useRef([]);
  const editGridApiRef = useRef(null);

  const { hasPermission: viewRates } = usePermissions(
    "view",
    "configuration.fsc.global-ibr-rates",
  );
  const { hasPermission: uploadRates } = usePermissions(
    "upload",
    "configuration.fsc.global-ibr-rates",
  );
  const { hasPermission: downloadRates } = usePermissions(
    "download",
    "configuration.fsc.global-ibr-rates",
  );
  const { hasPermission: editRates } = usePermissions(
    "edit",
    "configuration.fsc.global-ibr-rates",
  );
  const { hasPermission: deleteRates } = usePermissions(
    "delete",
    "configuration.fsc.global-ibr-rates",
  );

  const rateColumns = useMemo(() => [
    {
      field: "fromIsoCode",
      headerName: "From Currency (Currency Code)",
      flex: 1,
      minWidth: 150,
      resizable: true,
      valueGetter: (params) => {
        const iso = params.data?.fromIsoCode || params.data?.isoCode || "";
        const info = currencyCodes.code(iso);
        const numCode = info && info.number ? String(info.number).padStart(3, "0") : "";
        return numCode ? `${iso} (${numCode})` : iso;
      }
    },
    {
      field: "toIsoCode",
      headerName: "To Currency (Currency Code)",
      flex: 1,
      minWidth: 150,
      resizable: true,
      valueGetter: (params) => {
        const iso = params.data?.toIsoCode || getCultureFromLs()?.currency?.code || "KWD";
        const info = currencyCodes.code(iso);
        const numCode = info && info.number ? String(info.number).padStart(3, "0") : "";
        return numCode ? `${iso} (${numCode})` : iso;
      }
    },
    {
      field: "rate",
      headerName: "Exchange Rate",
      flex: 1,
      minWidth: 150,
      resizable: true,
    },
  ], []);

  const handleViewRates = async (rowData) => {
    try {
      setSelectedRateListMeta(rowData);
      setLoadingModalRates(true);
      setShowViewModal(true);
      setModalDirection("foreign_to_base");

      const res = await APIEndpoint.post("operations/ibrExchange/rates/by-id", { id: rowData.id });
      const fetchedRates = res?.data?.rates?.items || res?.rates?.items || [];
      setModalRates(fetchedRates);
    } catch (err) {
      console.error("Error fetching rates by id:", err);
      setModalRates([]);
    } finally {
      setLoadingModalRates(false);
    }
  };

  const handleOpenEditModal = async (rowData) => {
    try {
      setSelectedEditRateListMeta(rowData);
      setEditFormValues({
        name: rowData.name || "",
        effFrom: rowData.effFrom ? moment(rowData.effFrom, ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", moment.ISO_8601]).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD")
      });
      setEditError("");
      setEditSuccess("");
      setShowAddRowBar(false);
      setNewRowData({ fromIsoCode: "", rate: "" });
      setAddRowError("");
      setShowEditModal(true);
      setLoadingEditRates(true);

      const res = await APIEndpoint.post("operations/ibrExchange/rates/by-id", { id: rowData.id });
      const fetchedRates = res?.data?.rates?.items || res?.rates?.items || [];
      const baseCurrency = getCultureFromLs()?.currency?.code || "KWD";
      const normalizedRates = fetchedRates.map(it => {
        const fromIso = it.fromIsoCode || it.isoCode || "";
        const toIso = it.toIsoCode || baseCurrency;
        return {
          fromIsoCode: fromIso,
          isoCode: it.isoCode || fromIso,
          toIsoCode: toIso,
          rate: parseFloat(it.rate)
        };
      });
      const directRates = normalizedRates.filter(
        it => it.toIsoCode === baseCurrency || it.fromIsoCode !== baseCurrency
      );
      const ratesToSet = directRates.length > 0 ? directRates : normalizedRates;
      setEditModalRates(ratesToSet);
    } catch (err) {
      console.error("Error loading rates for edit:", err);
      setEditModalRates([]);
      setEditError("Failed to load rates for editing");
    } finally {
      setLoadingEditRates(false);
    }
  };

  const handleDeleteUpcomingRates = (rowData) => {
    setDeleteModalState({
      show: true,
      item: rowData,
      loading: false,
      statusObj: null,
    });
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalState({
      show: false,
      item: null,
      loading: false,
      statusObj: null,
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteModalState.item?.id) return;
    try {
      setDeleteModalState((prev) => ({ ...prev, loading: true, statusObj: null }));
      const res = await APIEndpoint.post("operations/ibrExchange/rates/delete", {
        id: deleteModalState.item.id,
      });
      if (res?.data?.success || res?.success) {
        setDeleteModalState((prev) => ({
          ...prev,
          loading: false,
          statusObj: { text: res?.data?.message || res?.message || "Upcoming rate list deleted successfully" },
        }));
        setTimeout(() => {
          handleCloseDeleteModal();
          setIsReloadTable((prev) => !prev);
        }, 1200);
      } else {
        setDeleteModalState((prev) => ({
          ...prev,
          loading: false,
          statusObj: { error: res?.data?.message || res?.message || "Failed to delete rate list" },
        }));
      }
    } catch (err) {
      console.error("Error deleting upcoming rates:", err);
      const errMsg =
        err?.response?.data?.message ||
        err?.data?.message ||
        err?.message ||
        "Failed to delete rate list";
      setDeleteModalState((prev) => ({
        ...prev,
        loading: false,
        statusObj: { error: errMsg },
      }));
    }
  };

  const mainTableColumns = useMemo(() => [
    {
      field: "name",
      headerName: "Name",
      flex: 1.5,
      minWidth: 180,
      resizable: true,
      cellStyle: { display: "flex", alignItems: "center" },
    },
    {
      field: "effFrom",
      headerName: "Effective Date",
      flex: 1,
      minWidth: 140,
      resizable: true,
      cellStyle: { display: "flex", alignItems: "center" },
      valueGetter: (params) => {
        if (!params.data?.effFrom) return "";
        const m = moment(params.data.effFrom, ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", moment.ISO_8601]);
        return m.isValid() ? m.format("DD-MM-YYYY") : params.data.effFrom;
      }
    },
    {
      field: "effUntil",
      headerName: "End Date",
      flex: 1,
      minWidth: 140,
      resizable: true,
      cellStyle: { display: "flex", alignItems: "center" },
      valueGetter: (params) => {
        if (!params.data) return "";
        if (params.data?.effUntil && params.data.effUntil !== "N/A") {
          const m = moment(params.data.effUntil, ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", moment.ISO_8601]);
          return m.isValid() ? m.format("DD-MM-YYYY") : params.data.effUntil;
        }
        return "N/A";
      }
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 130,
      resizable: true,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-start" },
      cellRenderer: (params) => {
        if (!params.data) return null;
        const status = (params.value || "closed").toLowerCase();
        let badgeStyle = {
          padding: "4px 12px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: "600",
          textTransform: "capitalize",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          lineHeight: "1.2",
          margin: "auto 0",
        };

        let label = "Closed";
        if (status === "active") {
          label = "Active";
          badgeStyle = {
            ...badgeStyle,
            backgroundColor: "rgba(16, 185, 129, 0.15)",
            color: "#10b981",
            border: "1px solid rgba(16, 185, 129, 0.3)"
          };
        } else if (status === "upcoming") {
          label = "Upcoming";
          badgeStyle = {
            ...badgeStyle,
            backgroundColor: "rgba(56, 189, 248, 0.15)",
            color: "#38bdf8",
            border: "1px solid rgba(56, 189, 248, 0.3)"
          };
        } else if (status === "overridden") {
          label = "Overridden";
          badgeStyle = {
            ...badgeStyle,
            backgroundColor: "rgba(245, 158, 11, 0.15)",
            color: "#f59e0b",
            border: "1px solid rgba(245, 158, 11, 0.3)"
          };
        } else {
          label = "Closed";
          badgeStyle = {
            ...badgeStyle,
            backgroundColor: "rgba(148, 163, 184, 0.15)",
            color: "#94a3b8",
            border: "1px solid rgba(148, 163, 184, 0.3)"
          };
        }

        return (
          <span style={badgeStyle}>
            {label}
          </span>
        );
      }
    },
    {
      field: "actions",
      headerName: "Action",
      flex: 0.8,
      minWidth: 110,
      cellStyle: { display: "flex", alignItems: "center" },
      cellRenderer: (params) => {
        if (!params.data) return null;
        const isUpcoming = (params.data?.status || "").toLowerCase() === "upcoming";
        const canEdit = editRates !== false && isUpcoming;
        const canDelete = deleteRates !== false && isUpcoming;

        return (
          <div
            className="icon-aircraft-container flexRow alignItemsCenter"
            style={{ cursor: "pointer", height: "100%", gap: "12px" }}
          >
            <div
              className="pointer flexRow"
              onClick={() => handleViewRates(params.data)}
              title="View Rates"
            >
              <img className="edit" alt="View Rates" src={viewIcon} style={{ width: "16px", height: "16px" }} />
            </div>
            {canEdit && (
              <div
                className="pointer flexRow"
                onClick={() => handleOpenEditModal(params.data)}
                title="Edit Upcoming Rates"
              >
                <img className="edit" alt="Edit Rates" src={editIcon2} style={{ width: "16px", height: "16px" }} />
              </div>
            )}
            {canDelete && (
              <div
                className="pointer flexRow"
                onClick={() => handleDeleteUpcomingRates(params.data)}
                title="Delete Upcoming Rates"
              >
                <img
                  className="edit"
                  alt="Delete Rates"
                  src={deleteIcon}
                  style={{
                    width: "30px",
                    height: "30px",
                    marginTop: "calc(0.5rem - 2px)",
                    marginLeft: "-4px",
                    marginRight: "-4px"
                  }}
                />
              </div>
            )}
          </div>
        );
      }
    }
  ], [editRates, deleteRates]);

  async function fetchRates(iPageNo = 1, iPageLimit = 50, search) {
    if (viewRates === false) {
      return { items: [], count: 0 };
    }
    try {
      const res = await APIEndpoint.post("operations/ibrExchange/rates/all", {});
      const rawLists = res?.data?.rateLists || res?.rateLists || [];
      allRateListsCache.current = rawLists;

      let items = rawLists.map((item) => ({
        id: item.id,
        name: item.name || (item.effFrom ? `IBR Rates ${moment(item.effFrom).format("DD-MM-YYYY")}` : (item.fileName || "IBR Rates")),
        effFrom: item.effFrom ? moment(item.effFrom).format("DD-MM-YYYY") : "",
        effUntil: item.effUntil ? moment(item.effUntil).format("DD-MM-YYYY") : "N/A",
        status: item.status || "closed",
        fileName: item.fileName || "IBR_Rates.csv",
        createdAt: item.createdAt ? moment(item.createdAt).format("DD-MM-YYYY HH:mm") : "",
        createdByName: item.createdBy?.employeeName || item.createdBy?.employeeCode || "Admin",
        totalRecords: item.totalRecords || (item.items ? item.items.length : 0),
        rawItem: item
      }));

      if (search) {
        const s = search.toLowerCase();
        items = items.filter(
          (item) =>
            item.name?.toLowerCase().includes(s) ||
            item.effFrom?.toLowerCase().includes(s) ||
            item.effUntil?.toLowerCase().includes(s) ||
            item.status?.toLowerCase().includes(s) ||
            item.createdByName?.toLowerCase().includes(s)
        );
      }

      const count = items.length;
      const startIndex = (iPageNo - 1) * iPageLimit;
      const paginatedItems = items.slice(startIndex, startIndex + iPageLimit);
      return { items: paginatedItems, count };
    } catch (error) {
      console.error("Error fetching IBR rate lists:", error);
      return { items: [], count: 0 };
    }
  }

  useEffect(() => {
    setIsReloadTable((prev) => !prev);
  }, [viewRates]);

  function onUploadClose() {
    setShowUpload(false);
    setUploadFileStatusBlock("");
    setUploadedFile([]);
    setFormDetails({});
  }

  async function handleUploadRates(e) {
    const file = e.target.files[0];
    if (!file) return;

    const name = (formDetails?.name || "").trim();
    const rawEffFrom = formDetails?.effFrom;
    const parsedEffFrom = rawEffFrom ? moment(rawEffFrom, ["DD-MM-YYYY", "DD/MM/YYYY", "YYYY-MM-DD", moment.ISO_8601]) : moment();
    const effFrom = parsedEffFrom.isValid() ? parsedEffFrom.format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");

    if (!name) {
      e.target.value = null;
      setUploadedFile([]);
      setUploadFileStatusBlock(
        "<p class='listErrorMessagesCls'>Name is required.</p>"
      );
      return;
    }

    // Check duplicate name
    const isDuplicate = allRateListsCache.current.some(
      (item) => (item.name || "").trim().toLowerCase() === name.toLowerCase()
    );
    if (isDuplicate) {
      e.target.value = null;
      setUploadedFile([]);
      setUploadFileStatusBlock(
        `<p class='listErrorMessagesCls'>A rate list with the name '${name}' already exists.</p>`
      );
      return;
    }

    const today = moment().startOf('day');
    const startDate = moment(effFrom).startOf('day');

    if (startDate.isBefore(today)) {
      e.target.value = null;
      setUploadedFile([]);
      setUploadFileStatusBlock(
        "<p class='listErrorMessagesCls'>Effective Date cannot be a past date.</p>"
      );
      return;
    }

    setUploadedFile(file);
    setUploadFileStatusBlock(
      "<p>Please wait while we process your data...</p>"
    );

    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", name);
    formData.append("effFrom", effFrom);

    try {
      const res = await APIEndpoint.post(
        "ibrExchange/rates/upload",
        formData,
        { 'Content-Type': "multipart/form-data" }
      );

      if (res && (res.success || res.data?.success)) {
        allRateListsCache.current = [];
        setUploadFileStatusBlock("<p>File Imported Successfully.</p>");
        setTimeout(() => {
          onUploadClose();
          setIsReloadTable((prev) => !prev);
        }, 1500);
      } else {
        e.target.value = null;
        setUploadedFile([]);
        const errMsg = res?.message || res?.data?.message || "Failed to upload.";
        const errorsList = res?.errors || res?.data?.errors;
        let msgContent = "";
        if (Array.isArray(errorsList)) {
          errorsList.forEach((_) => {
            const lineNum = _.lineNumber;
            const errorMsgArr = [];
            (_.errors || []).forEach(erObj => {
              const fieldText = erObj.field ? `${erObj.field} | ` : '';
              errorMsgArr.push(fieldText + erObj.message + ' \n<br>');
            });
            msgContent += '<br>Line ' + lineNum + ': \n<br>' + errorMsgArr.join(' ');
          });
        }
        setUploadFileStatusBlock(
          "<p class='listErrorMessagesCls'>" + errMsg + "</p>" +
          (msgContent ? "<p class='listErrorMessagesCls sm-scrollbar border p-2 pt-1'>" + msgContent + "</p>" : "")
        );
      }
    } catch (error) {
      e.target.value = null;
      setUploadedFile([]);
      let status = 0;
      let data = null;
      try {
        const parsed = JSON.parse(error.message);
        status = parsed.status;
        data = parsed.data;
      } catch (parseErr) {
        data = error?.response?.data || null;
        status = error?.response?.status || 0;
      }

      let msgHeading = data?.Error || data?.message || error.message || "Failed to upload.";
      msgHeading = msgHeading.includes('status') ? 'Failed to upload.' : msgHeading;

      let msgContent = "";
      if (status === 400 && data && data.errors && Array.isArray(data.errors)) {
        data.errors.forEach((_) => {
          const lineNum = _.lineNumber;
          const errorMsgArr = [];
          (_.errors || []).forEach(erObj => {
            const fieldText = erObj.field ? `${erObj.field} | ` : '';
            errorMsgArr.push(fieldText + erObj.message + ' \n<br>');
          });
          msgContent += '<br>Line ' + lineNum + ': \n<br>' + errorMsgArr.join(' ');
        });
      } else if (status === 0 && !data) {
        msgContent += 'Error occurred while uploading IBR rates sheet!<br>Please try again.';
      } else {
        msgContent += 'Error occurred while uploading IBR rates sheet!<br>Please fix the errors and re-upload.';
      }

      setUploadFileStatusBlock(
        "<p class='listErrorMessagesCls'>" + msgHeading + "</p>" +
        (msgContent ? "<p class='listErrorMessagesCls sm-scrollbar border p-2 pt-1'>" + msgContent + "</p>" : "")
      );
    }
  }

  const handleDownloadModalRates = () => {
    if (!selectedRateListMeta || !modalRates.length) return;
    const items = filterRatesByDirection(modalRates, modalDirection);
    let csvContent = "ISO Code,Currency Code,Rate\n";
    items.forEach((item) => {
      const iso = item.fromIsoCode || "";
      const info = currencyCodes.code(iso);
      const numCode = info && info.number ? String(info.number).padStart(3, "0") : "";
      csvContent += `${iso},${numCode},${item.rate ?? ""}\n`;
    });
    const safeName = (selectedRateListMeta.name || "IBR_Rates").replace(/[^a-z0-9_-]/gi, "_");
    const fileName = `${safeName}_${modalDirection}.csv`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, fileName);
  };

  const onDownloadSampleFile = () => {
    const csvContent = "ISO Code,Currency Code,Rate\nUSD,840,0.308000\nEUR,978,0.334000\nGBP,826,0.392000\nINR,356,0.003700\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "IBR_Exchange_Rates_Sample.csv");
  };

  // Handler for replacing rates via file in Edit Modal
  const handleReplaceRatesFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const text = evt.target.result;
        const lines = text.split(/\r\n|\n/).map(l => l.trim()).filter(Boolean);
        if (lines.length < 2) {
          setEditError("CSV file is empty or missing data rows.");
          return;
        }
        const headers = lines[0].split(',').map(h => h.replace(/^["']|["']$/g, '').trim().toLowerCase());
        let isoIdx = headers.indexOf('iso code');
        let rateIdx = headers.indexOf('rate');
        const missingHeaders = [];
        if (isoIdx === -1) {
          missingHeaders.push('ISO Code');
          isoIdx = headers.findIndex(h => h === 'iso' || h.includes('from') || h === 'currency');
          if (isoIdx === -1 && headers.length > 0) isoIdx = 0;
        }
        if (rateIdx === -1) {
          missingHeaders.push('Rate');
          rateIdx = headers.findIndex(h => h.includes('rate') || h.includes('exchange'));
          if (rateIdx === -1 && headers.length > 1) rateIdx = headers.length > 2 ? 2 : 1;
        }

        const baseCurrency = getCultureFromLs()?.currency?.code || "KWD";
        const newRates = [];
        const seen = new Set();
        const lineErrors = [];

        if (missingHeaders.length > 0) {
          lineErrors.push(`Line 1 (Header): Missing or invalid column header(s): ${missingHeaders.join(', ')}. Expected: 'ISO Code', 'Rate'.`);
        }

        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;
          const parts = line.split(',').map(c => c.replace(/^["']|["']$/g, '').trim());
          const iso = (parts[isoIdx] || '').toUpperCase();
          const rateVal = parseFloat(parts[rateIdx]);

          if (!iso) {
            lineErrors.push(`Line ${i + 1}: Missing ISO currency code.`);
            continue;
          }
          if (!currencyCodes.code(iso)) {
            lineErrors.push(`Line ${i + 1}: '${iso}' is not a valid ISO 4217 currency code.`);
            continue;
          }
          if (iso === baseCurrency.toUpperCase()) {
            continue; // Skip base currency against itself (e.g. KWD -> KWD)
          }
          if (seen.has(iso)) {
            lineErrors.push(`Line ${i + 1}: Duplicate ISO currency code '${iso}' found in CSV.`);
            continue;
          }
          if (isNaN(rateVal) || rateVal <= 0) {
            lineErrors.push(`Line ${i + 1}: Invalid exchange rate '${parts[rateIdx]}' for '${iso}'. Must be a positive number.`);
            continue;
          }

          seen.add(iso);
          newRates.push({
            fromIsoCode: iso,
            isoCode: iso,
            toIsoCode: baseCurrency,
            rate: rateVal
          });
        }

        if (lineErrors.length > 0) {
          setEditError(lineErrors.join("\n"));
          return;
        }

        if (newRates.length === 0) {
          setEditError("No valid rate rows found in the uploaded file.");
          return;
        }

        setEditModalRates(newRates);
        if (editGridApiRef.current) {
          editGridApiRef.current.setRowData(newRates);
        }
        setEditError("");
        setEditSuccess(`Successfully loaded ${newRates.length} rates from file. Click 'Save Changes' to update.`);
      } catch (err) {
        setEditError("Failed to parse CSV file: " + err.message);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  // Cell change handler in Edit Modal AgGrid
  const onEditCellValueChanged = (params) => {
    const newVal = parseFloat(params.newValue);
    if (isNaN(newVal) || newVal <= 0) {
      setEditError(`Invalid rate '${params.newValue}'. Must be a positive number.`);
      return;
    }
    setEditError("");
    params.data.rate = newVal;
    const targetIso = (params.data.fromIsoCode || params.data.isoCode || "").toUpperCase();
    setEditModalRates(prev => {
      const next = [...prev];
      const idx = next.findIndex(
        r => (r.fromIsoCode || r.isoCode || "").toUpperCase() === targetIso
      );
      if (idx !== -1) {
        next[idx] = { ...next[idx], rate: newVal };
      }
      return next;
    });
  };

  // Delete row in Edit Modal grid
  const handleDeleteEditRow = (rowData, rowIndex) => {
    const targetIso = (rowData?.fromIsoCode || rowData?.isoCode || "").toUpperCase();
    if (editGridApiRef.current) {
      editGridApiRef.current.stopEditing();
      const currentRates = [];
      editGridApiRef.current.forEachNode(node => {
        if (node.data) currentRates.push({ ...node.data });
      });
      if (currentRates.length > 0) {
        const filtered = targetIso
          ? currentRates.filter(r => (r.fromIsoCode || r.isoCode || "").toUpperCase() !== targetIso)
          : currentRates.filter((_, idx) => idx !== rowIndex);
        setEditModalRates(filtered);
        editGridApiRef.current.setRowData(filtered);
        return;
      }
    }
    setEditModalRates(prev => {
      const filtered = targetIso
        ? prev.filter(r => (r.fromIsoCode || r.isoCode || "").toUpperCase() !== targetIso)
        : prev.filter((_, idx) => idx !== rowIndex);
      if (editGridApiRef.current) {
        editGridApiRef.current.setRowData(filtered);
      }
      return filtered;
    });
  };

  // Add currency row in Edit Modal
  const handleAddCurrencyRow = () => {
    const code = (newRowData.fromIsoCode || "").trim().toUpperCase();
    const rawRate = (newRowData.rate !== undefined && newRowData.rate !== null) ? String(newRowData.rate).trim() : "";
    const rateNum = parseFloat(rawRate);
    const baseCurrency = getCultureFromLs()?.currency?.code || "KWD";

    if (!code) {
      setAddRowError("Currency code is required.");
      return;
    }
    const isoInfo = currencyCodes.code(code);
    if (!isoInfo) {
      setAddRowError(`'${code}' is not a valid ISO 4217 currency code.`);
      return;
    }
    if (code === baseCurrency.toUpperCase()) {
      setAddRowError(`Currency code cannot be the base currency (${baseCurrency}).`);
      return;
    }
    if (isNaN(rateNum) || rateNum <= 0) {
      setAddRowError("Exchange rate must be a positive number.");
      return;
    }

    let currentRates = [];
    if (editGridApiRef.current) {
      editGridApiRef.current.stopEditing();
      editGridApiRef.current.forEachNode(node => {
        if (node.data) currentRates.push({ ...node.data });
      });
    }
    if (currentRates.length === 0) {
      currentRates = [...editModalRates];
    }

    const exists = currentRates.some(
      r => (r.fromIsoCode || r.isoCode || "").toUpperCase() === code
    );
    if (exists) {
      setAddRowError(`Currency '${code}' already exists in this rate list.`);
      return;
    }

    const newRecord = {
      fromIsoCode: code,
      isoCode: code,
      toIsoCode: baseCurrency,
      rate: rateNum
    };

    const updatedRates = [newRecord, ...currentRates];
    setEditModalRates(updatedRates);
    if (editGridApiRef.current) {
      editGridApiRef.current.setRowData(updatedRates);
    }
    setNewRowData({ fromIsoCode: "", rate: "" });
    setAddRowError("");
    setShowAddRowBar(false);
  };

  const editGridColumns = useMemo(() => [
    {
      field: "fromIsoCode",
      headerName: "From Currency",
      flex: 1,
      minWidth: 140,
      resizable: true,
      editable: false,
    },
    {
      field: "toIsoCode",
      headerName: "To Currency",
      flex: 1,
      minWidth: 140,
      resizable: true,
      editable: false,
    },
    {
      field: "rate",
      headerName: "Exchange Rate (Click to Edit)",
      flex: 1.2,
      minWidth: 180,
      resizable: true,
      editable: true,
      cellStyle: {
        cursor: "pointer",
        backgroundColor: "rgba(56, 189, 248, 0.08)",
        fontWeight: "500",
        border: "1px dashed rgba(56, 189, 248, 0.3)"
      }
    },
    {
      field: "actions",
      headerName: "Action",
      width: 80,
      minWidth: 80,
      maxWidth: 90,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
      cellRenderer: (params) => {
        return (
          <div
            className="pointer flexRow alignItemsCenter justifyContentCenter"
            onClick={() => handleDeleteEditRow(params.data, params.node?.rowIndex ?? params.rowIndex)}
            title="Remove Currency"
            style={{ cursor: "pointer", height: "100%" }}
          >
            <img
              alt="Delete"
              src={deleteIcon}
              style={{
                width: "20px",
                height: "20px",
                cursor: "pointer"
              }}
            />
          </div>
        );
      }
    }
  ], []);

  // Save changes in Edit Modal
  const handleSaveEdit = async () => {
    // 1. Force AgGrid to stop editing any currently focused cell so all edits are committed
    if (editGridApiRef.current) {
      editGridApiRef.current.stopEditing();
    }

    const newName = (editFormValues.name || "").trim();
    const newEffFrom = editFormValues.effFrom;

    if (!newName) {
      setEditError("Name is required.");
      return;
    }

    // Duplicate check among OTHER rate lists
    const duplicate = allRateListsCache.current.find(
      l => l.id !== selectedEditRateListMeta.id && (l.name || "").trim().toLowerCase() === newName.toLowerCase()
    );
    if (duplicate) {
      setEditError(`A rate list with the name '${newName}' already exists.`);
      return;
    }

    const today = moment().startOf('day');
    const start = moment(newEffFrom, ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", moment.ISO_8601]).startOf('day');
    if (!start.isValid() || start.isBefore(today)) {
      setEditError("Effective Date cannot be a past date.");
      return;
    }

    // 2. Extract live rates directly from AgGrid table nodes to avoid any React state race conditions
    let ratesToSend = [];
    if (editGridApiRef.current) {
      editGridApiRef.current.forEachNode(node => {
        if (node.data) {
          ratesToSend.push({
            fromIsoCode: node.data.fromIsoCode || node.data.isoCode,
            isoCode: node.data.isoCode || node.data.fromIsoCode,
            toIsoCode: node.data.toIsoCode || getCultureFromLs()?.currency?.code || "KWD",
            rate: parseFloat(node.data.rate)
          });
        }
      });
    }

    if (ratesToSend.length === 0) {
      ratesToSend = editModalRates.map(r => ({
        fromIsoCode: r.fromIsoCode || r.isoCode,
        isoCode: r.isoCode || r.fromIsoCode,
        toIsoCode: r.toIsoCode || getCultureFromLs()?.currency?.code || "KWD",
        rate: parseFloat(r.rate)
      }));
    }

    if (!ratesToSend || ratesToSend.length === 0) {
      setEditError("Rate list must contain at least one valid exchange rate.");
      return;
    }

    for (const item of ratesToSend) {
      const iso = (item.fromIsoCode || item.isoCode || "").trim().toUpperCase();
      if (!iso || !currencyCodes.code(iso)) {
        setEditError(`'${item.fromIsoCode || item.isoCode}' is not a valid ISO 4217 currency code.`);
        return;
      }
      const r = parseFloat(item.rate);
      if (isNaN(r) || r <= 0) {
        setEditError(`Invalid rate '${item.rate}' for ${iso}. Must be a positive number.`);
        return;
      }
    }

    try {
      setSavingEdit(true);
      setEditError("");
      setEditSuccess("");

      const res = await APIEndpoint.post("operations/ibrExchange/rates/update", {
        id: selectedEditRateListMeta.id,
        name: newName,
        effFrom: newEffFrom,
        items: ratesToSend
      });

      if (res && (res.success || res.data?.success)) {
        setEditSuccess("Upcoming IBR rate updated successfully.");
        allRateListsCache.current = [];
        setTimeout(() => {
          setShowEditModal(false);
          setIsReloadTable(prev => !prev);
        }, 1200);
      } else {
        setEditError(res?.message || res?.data?.message || "Failed to update rates.");
      }
    } catch (err) {
      let errMsg = "Failed to update rate list.";
      try {
        const parsed = JSON.parse(err.message);
        errMsg = parsed?.data?.message || parsed?.data?.error || err.message;
      } catch (e) {
        errMsg = err?.response?.data?.message || err?.message || "Failed to update rate list.";
      }
      setEditError(errMsg);
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="op-aircraft-container flexCol full-flex ag-theme-alpine-dark">
      {viewRates && (
        <DataGrid
          className="full-flex"
          columns={mainTableColumns}
          showDefaultFilters={true}
          getRows={fetchRates}
          columnResize={true}
          resetDataSource={isReloadTable}
          RightComponent={() => {
            return (
              <div
                className="flexRow justifyContentFlexEnd alignItemsCenter"
                style={{ gap: "10px" }}
              >
                {uploadRates ? (
                  <button
                    className="add-item-button"
                    onClick={() => setShowUpload(true)}
                  >
                    + Add IBR Rates
                  </button>
                ) : null}
              </div>
            );
          }}
        />
      )}

      {/* Upload Modal */}
      {showUpload && (
        <UploadModal
          showFieldsInColumn={true}
          onUploadFileChangeHandler={handleUploadRates}
          modelTitle={"Upload IBR Rates"}
          onUploadClose={onUploadClose}
          onDownloadSampleFile={onDownloadSampleFile}
          uploadFileStatusBlock={uploadFileStatusBlock}
          uploadedFile={uploadedFile}
          setFormDetails={setFormDetails}
          inputFields={[
            {
              attribute: "name",
              label: "Name",
              type: "text",
              required: true,
              placeholder: "Enter Rate List Name",
              style: {
                height: "36px",
                minHeight: "36px",
                padding: "6px 12px",
                margin: "0.6rem 0rem",
                width: "200px",
                boxSizing: "border-box",
              },
              className: "width100 textAlignLeft",
            },
            {
              attribute: "effFrom",
              label: "Effective Date",
              type: "date",
              required: true,
              minDate: moment().startOf('day').toDate(),
              dateFormat: "dd-MM-yyyy",
              placeholder: "DD-MM-YYYY",
              width: "200px",
              style: {
                margin: "0.6rem 0rem",
                width: "200px",
              },
              className: "width100 textAlignLeft",
            },
          ]}
        />
      )}

      {/* View Modal */}
      {showViewModal && (
        <Modal
          title={`Viewing Rates - ${selectedRateListMeta?.name || 'IBR Rates'}`}
          onClose={() => setShowViewModal(false)}
          showDone={false}
          size="large"
        >
          <div className="flexCol width100 gap10" style={{ padding: "15px", boxSizing: "border-box" }}>
            <div
              className="flexRow justifyContentSpaceBetween alignItemsCenter width100 flexWrap"
              style={{
                background: "rgba(255,255,255,0.05)",
                padding: "12px 18px",
                borderRadius: "6px",
                fontSize: "13px",
                gap: "10px",
                width: "100%",
                boxSizing: "border-box"
              }}
            >
              <div><strong>Name:</strong> {selectedRateListMeta?.name}</div>
              <div><strong>Effective Date:</strong> {selectedRateListMeta?.effFrom ? (moment(selectedRateListMeta.effFrom, ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", moment.ISO_8601]).isValid() ? moment(selectedRateListMeta.effFrom, ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", moment.ISO_8601]).format("DD-MM-YYYY") : selectedRateListMeta.effFrom) : "N/A"}</div>
              <div><strong>End Date:</strong> {selectedRateListMeta?.effUntil && selectedRateListMeta.effUntil !== "N/A" ? (moment(selectedRateListMeta.effUntil, ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", moment.ISO_8601]).isValid() ? moment(selectedRateListMeta.effUntil, ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", moment.ISO_8601]).format("DD-MM-YYYY") : selectedRateListMeta.effUntil) : "N/A"}</div>
              <div>
                <strong>Status:</strong>{" "}
                <span
                  style={{
                    padding: "2px 8px",
                    borderRadius: "10px",
                    fontSize: "11px",
                    fontWeight: "600",
                    textTransform: "capitalize",
                    backgroundColor:
                      selectedRateListMeta?.status === "active"
                        ? "rgba(16, 185, 129, 0.2)"
                        : selectedRateListMeta?.status === "upcoming"
                        ? "rgba(56, 189, 248, 0.2)"
                        : selectedRateListMeta?.status === "overridden"
                        ? "rgba(245, 158, 11, 0.2)"
                        : "rgba(148, 163, 184, 0.2)",
                    color:
                      selectedRateListMeta?.status === "active"
                        ? "#10b981"
                        : selectedRateListMeta?.status === "upcoming"
                        ? "#38bdf8"
                        : selectedRateListMeta?.status === "overridden"
                        ? "#f59e0b"
                        : "#94a3b8"
                  }}
                >
                  {selectedRateListMeta?.status === "active"
                    ? "Active"
                    : selectedRateListMeta?.status === "upcoming"
                    ? "Upcoming"
                    : selectedRateListMeta?.status === "overridden"
                    ? "Overridden"
                    : "Closed"}
                </span>
              </div>
              <div><strong>Uploaded By:</strong> {selectedRateListMeta?.createdByName} ({selectedRateListMeta?.createdAt})</div>
              <div><strong>Records:</strong> {selectedRateListMeta?.totalRecords || modalRates.length}</div>
            </div>

            <div
              className="flexRow justifyContentSpaceBetween alignItemsCenter width100"
              style={{ marginTop: "10px", marginBottom: "10px", width: "100%" }}
            >
              <div className="flexRow alignItemsCenter">
                <CustomSelect
                  options={[
                    { value: "foreign_to_base", label: "Foreign → Base" },
                    { value: "base_to_foreign", label: "Base → Foreign" },
                  ]}
                  value={[
                    { value: "foreign_to_base", label: "Foreign → Base" },
                    { value: "base_to_foreign", label: "Base → Foreign" },
                  ].find((o) => o.value === modalDirection)}
                  onChange={(selected) => setModalDirection(selected.value)}
                  isSearchable={false}
                  isClearable={false}
                />
              </div>

              <div className="flexRow alignItemsCenter">
                <button
                  className="dropdownStyle1-TextMedium"
                  title="Download Rates as CSV"
                  onClick={handleDownloadModalRates}
                >
                  <span>
                    <img
                      className="icon-size-min"
                      alt="downloadMaster"
                      src={downloadMasterIcon}
                    />
                  </span>
                </button>
              </div>
            </div>

            {loadingModalRates ? (
              <div style={{ padding: "30px", textAlign: "center" }}>Loading rates...</div>
            ) : (
              <div className="ag-theme-alpine-dark width100" style={{ height: "360px", width: "100%" }}>
                <AgGridReact
                  id="ngo-grid"
                  columnDefs={rateColumns}
                  rowData={filterRatesByDirection(modalRates, modalDirection)}
                  className="table-header ngo-grid"
                  rowModelType="clientSide"
                  animateRows={true}
                />
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Edit Modal (for Upcoming Rates) */}
      {showEditModal && (
        <Modal
          title={`Edit Upcoming Rates - ${selectedEditRateListMeta?.name || 'IBR Rates'}`}
          onClose={() => setShowEditModal(false)}
          showDone={false}
          size="large"
        >
          <div className="flexCol width100 gap10" style={{ padding: "15px", boxSizing: "border-box" }}>
            {/* Top Form Fields: Name and Effective Date */}
            <div
              className="flexRow justifyContentSpaceBetween alignItemsCenter width100 flexWrap"
              style={{
                background: "rgba(255,255,255,0.03)",
                padding: "14px 18px",
                borderRadius: "6px",
                gap: "16px",
                width: "100%",
                boxSizing: "border-box"
              }}
            >
              <div className="flexCol" style={{ flex: 1, minWidth: "220px" }}>
                <label className="fontRegular req" style={{ marginBottom: "6px", fontSize: "13px" }}>
                  Name
                </label>
                <input
                  type="text"
                  className="edit-input"
                  style={{
                    width: "100%",
                    height: "36px",
                    padding: "6px 12px",
                    borderRadius: "4px",
                    backgroundColor: "rgba(255,255,255,0.06)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.15)"
                  }}
                  value={editFormValues.name}
                  onChange={(e) => setEditFormValues(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter Rate List Name"
                />
              </div>

              <div className="flexCol" style={{ flex: 1, minWidth: "220px" }}>
                <label className="fontRegular req" style={{ marginBottom: "6px", fontSize: "13px" }}>
                  Effective Date
                </label>
                <NGODatePicker
                  value={editFormValues.effFrom ? moment(editFormValues.effFrom, ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", moment.ISO_8601]).toDate() : null}
                  minDate={moment().startOf('day').toDate()}
                  dateFormat="dd-MM-yyyy"
                  placeholder="DD-MM-YYYY"
                  isclearable={false}
                  onChange={(e) => {
                    const rawVal = e?.target !== undefined ? e.target.value : e;
                    let newDate = "";
                    if (rawVal instanceof Date) {
                      newDate = moment(rawVal).format("YYYY-MM-DD");
                    } else if (rawVal) {
                      const m = moment(rawVal, ["DD-MM-YYYY", "DD/MM/YYYY", "YYYY-MM-DD"], true);
                      newDate = m.isValid() ? m.format("YYYY-MM-DD") : moment(rawVal).format("YYYY-MM-DD");
                    }
                    setEditFormValues(prev => ({ ...prev, effFrom: newDate }));
                  }}
                />
              </div>
            </div>

            {/* Error / Success Feedback */}
            {editError && (
              <div
                style={{
                  color: "#ef4444",
                  backgroundColor: "rgba(239, 68, 68, 0.1)",
                  border: "1px solid rgba(239, 68, 68, 0.3)",
                  padding: "8px 14px",
                  borderRadius: "4px",
                  fontSize: "13px"
                }}
              >
                {editError}
              </div>
            )}
            {editSuccess && (
              <div
                style={{
                  color: "#10b981",
                  backgroundColor: "rgba(16, 185, 129, 0.1)",
                  border: "1px solid rgba(16, 185, 129, 0.3)",
                  padding: "8px 14px",
                  borderRadius: "4px",
                  fontSize: "13px"
                }}
              >
                {editSuccess}
              </div>
            )}

            {/* Grid Header & Replace Button */}
            <div
              className="flexRow justifyContentSpaceBetween alignItemsCenter width100"
              style={{ marginTop: "6px", width: "100%" }}
            >
              <div style={{ fontSize: "13px", fontWeight: "600" }}>
                Exchange Rates ({editModalRates.length} records)
                <span style={{ fontSize: "11px", fontWeight: "400", color: "#94a3b8", marginLeft: "8px" }}>
                  (Click any rate cell to edit directly)
                </span>
              </div>
              <div className="flexRow alignItemsCenter" style={{ gap: "10px" }}>
                <button
                  type="button"
                  className="btn btn-secondary flexRow alignItemsCenter"
                  onClick={() => {
                    setShowAddRowBar(prev => !prev);
                    setAddRowError("");
                  }}
                  style={{
                    margin: 0,
                    padding: "6px 14px",
                    cursor: "pointer",
                    borderRadius: "4px",
                    backgroundColor: showAddRowBar ? "rgba(2, 132, 199, 0.2)" : "rgba(255,255,255,0.08)",
                    border: showAddRowBar ? "1px solid #0284c7" : "1px solid rgba(255,255,255,0.15)",
                    color: "#fff",
                    fontSize: "12px",
                    fontWeight: "500",
                    height: "32px"
                  }}
                >
                  <span style={{ marginRight: "4px", fontSize: "14px", fontWeight: "bold" }}>+</span>
                  <span>Add Currency</span>
                </button>

                <label
                  className="dropdownStyle1-TextMedium pointer flexRow alignItemsCenter"
                  title="Upload CSV to replace current rates"
                  style={{ margin: 0, padding: "6px 14px", cursor: "pointer", height: "32px", display: "inline-flex", alignItems: "center" }}
                >
                  <img className="icon-size-min" alt="replace" src={uploadItemIcon} style={{ marginRight: "6px" }} />
                  <span>Replace via CSV</span>
                  <input
                    type="file"
                    accept=".csv"
                    style={{ display: "none" }}
                    onChange={handleReplaceRatesFile}
                  />
                </label>
              </div>
            </div>

            {/* Add Currency Bar */}
            {showAddRowBar && (
              <div
                className="flexCol width100"
                style={{
                  background: "rgba(255, 255, 255, 0.04)",
                  border: "1px solid rgba(2, 132, 199, 0.3)",
                  borderRadius: "6px",
                  padding: "12px 16px",
                  marginTop: "8px",
                  gap: "10px",
                  boxSizing: "border-box"
                }}
              >
                <div className="flexRow justifyContentSpaceBetween alignItemsCenter width100 flexWrap" style={{ gap: "12px" }}>
                  <div className="flexCol" style={{ flex: 1, minWidth: "150px" }}>
                    <label style={{ fontSize: "12px", color: "#cbd5e1", marginBottom: "4px" }}>
                      From Currency (ISO) *
                    </label>
                    <input
                      type="text"
                      maxLength={3}
                      placeholder="e.g. USD"
                      value={newRowData.fromIsoCode}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3);
                        setNewRowData(prev => ({ ...prev, fromIsoCode: val }));
                        setAddRowError("");
                      }}
                      style={{
                        height: "32px",
                        padding: "4px 10px",
                        borderRadius: "4px",
                        backgroundColor: "rgba(255,255,255,0.08)",
                        color: "#fff",
                        border: "1px solid rgba(255,255,255,0.2)",
                        fontSize: "13px",
                        textTransform: "uppercase"
                      }}
                    />
                    {newRowData.fromIsoCode && newRowData.fromIsoCode.length === 3 && (
                      <span
                        style={{
                          fontSize: "11px",
                          marginTop: "3px",
                          color: currencyCodes.code(newRowData.fromIsoCode) ? "#38bdf8" : "#ef4444"
                        }}
                      >
                        {currencyCodes.code(newRowData.fromIsoCode)
                          ? `${currencyCodes.code(newRowData.fromIsoCode).currency} (${currencyCodes.code(newRowData.fromIsoCode).number || ""})`
                          : "Not a valid ISO 4217 code"}
                      </span>
                    )}
                  </div>

                  <div className="flexCol" style={{ flex: 1, minWidth: "150px" }}>
                    <label style={{ fontSize: "12px", color: "#cbd5e1", marginBottom: "4px" }}>
                      To Currency (Base)
                    </label>
                    <input
                      type="text"
                      disabled
                      value={getCultureFromLs()?.currency?.code || "KWD"}
                      style={{
                        height: "32px",
                        padding: "4px 10px",
                        borderRadius: "4px",
                        backgroundColor: "rgba(255,255,255,0.03)",
                        color: "#94a3b8",
                        border: "1px solid rgba(255,255,255,0.1)",
                        fontSize: "13px"
                      }}
                    />
                  </div>

                  <div className="flexCol" style={{ flex: 1, minWidth: "150px" }}>
                    <label style={{ fontSize: "12px", color: "#cbd5e1", marginBottom: "4px" }}>
                      Exchange Rate *
                    </label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      placeholder="e.g. 0.308"
                      value={newRowData.rate}
                      onChange={(e) => {
                        setNewRowData(prev => ({ ...prev, rate: e.target.value }));
                        setAddRowError("");
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleAddCurrencyRow();
                        }
                      }}
                      style={{
                        height: "32px",
                        padding: "4px 10px",
                        borderRadius: "4px",
                        backgroundColor: "rgba(255,255,255,0.08)",
                        color: "#fff",
                        border: "1px solid rgba(255,255,255,0.2)",
                        fontSize: "13px"
                      }}
                    />
                  </div>

                  <div className="flexRow alignItemsFlexEnd" style={{ gap: "8px", marginTop: "18px" }}>
                    <button
                      type="button"
                      onClick={handleAddCurrencyRow}
                      style={{
                        height: "32px",
                        padding: "0 16px",
                        borderRadius: "4px",
                        backgroundColor: "#0284c7",
                        color: "#fff",
                        border: "none",
                        fontWeight: "600",
                        fontSize: "12px",
                        cursor: "pointer"
                      }}
                    >
                      Add Row
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddRowBar(false);
                        setAddRowError("");
                        setNewRowData({ fromIsoCode: "", rate: "" });
                      }}
                      style={{
                        height: "32px",
                        padding: "0 12px",
                        borderRadius: "4px",
                        backgroundColor: "rgba(255,255,255,0.08)",
                        color: "#94a3b8",
                        border: "none",
                        fontSize: "12px",
                        cursor: "pointer"
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>

                {addRowError && (
                  <div style={{ color: "#ef4444", fontSize: "12px" }}>
                    {addRowError}
                  </div>
                )}
              </div>
            )}

            {/* Editable AgGrid */}
            {loadingEditRates ? (
              <div style={{ padding: "30px", textAlign: "center" }}>Loading rates...</div>
            ) : (
              <div className="ag-theme-alpine-dark width100" style={{ height: "300px", width: "100%" }}>
                <AgGridReact
                  id="ngo-edit-grid"
                  columnDefs={editGridColumns}
                  rowData={editModalRates}
                  className="table-header ngo-grid"
                  rowModelType="clientSide"
                  animateRows={true}
                  onCellValueChanged={onEditCellValueChanged}
                  singleClickEdit={true}
                  onGridReady={(params) => {
                    editGridApiRef.current = params.api;
                  }}
                />
              </div>
            )}

            {/* Modal Actions */}
            <div
              className="flexRow justifyContentFlexEnd alignItemsCenter width100"
              style={{ marginTop: "12px", gap: "10px" }}
            >
              <button
                className="btn btn-secondary"
                onClick={() => setShowEditModal(false)}
                disabled={savingEdit}
                style={{
                  padding: "6px 16px",
                  borderRadius: "4px",
                  backgroundColor: "rgba(255,255,255,0.08)",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveEdit}
                disabled={savingEdit || loadingEditRates}
                style={{
                  padding: "6px 20px",
                  borderRadius: "4px",
                  backgroundColor: "#0284c7",
                  color: "#fff",
                  border: "none",
                  fontWeight: "600",
                  cursor: savingEdit ? "not-allowed" : "pointer"
                }}
              >
                {savingEdit ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal for Upcoming Rates */}
      {deleteModalState.show && (
        <DeleteConf
          description={`Are you sure you want to delete upcoming rate list "${deleteModalState.item?.name || ""}"?`}
          onCloseDelete={handleCloseDeleteModal}
          onContinue={handleConfirmDelete}
          buttonStat={deleteModalState.loading}
          modalFormStatusObj={deleteModalState.statusObj}
        />
      )}
    </div>
  );
};